// BlendShapeMapper.js (exact-match only, PERFORMANCE + COVERAGE upgrade)
// - Keeps your "protectedEmotionShapes" rule 100% intact.
// - Still exact-match only (prefix + variant must exist).
// - Adds fast lookup (Set/Map) to reduce per-frame cost.
// - Adds safer logging + dedupe.
// - Adds a tiny bit more variant coverage without guessing names.

export class BlendShapeMapper {
  constructor() {
    this._debug = false;
    this._maxLogsPerMap = 8;

    // Emotion shapes to never touch (and eye look shapes also protected)
    this.protectedEmotionShapes = [
      // Convention 1
      "BlendShapes_Main.happy_M",
      "BlendShapes_EyeLashes.happy_M",
      "BlendShapes_Eyebrows.happy_M",
      "BlendShapes_Beard.happy_M",
      "BlendShapes_Moustache.happy_M",
      "BlendShapes_Mouth.happy_M",
      "BlendShapes_Main.sad_M",
      "BlendShapes_EyeLashes.sad_M",
      "BlendShapes_Eyebrows.sad_M",
      "BlendShapes_Beard.sad_M",
      "BlendShapes_Moustache.sad_M",
      "BlendShapes_Main.angry_M",
      "BlendShapes_EyeLashes.angry_M",
      "BlendShapes_Eyebrows.angry_M",
      "BlendShapes_Beard.angry_M",
      "BlendShapes_Moustache.angry_M",
      "BlendShapes_Main.surprise_M",
      "BlendShapes_EyeLashes.surprise_M",
      "BlendShapes_Eyebrows.surprise_M",
      "BlendShapes_Beard.surprise_M",
      "BlendShapes_Moustache.surprise_M",
      "BlendShapes_Mouth.surprise_M",
      "BlendShapes_Main.fear_M",
      "BlendShapes_EyeLashes.fear_M",
      "BlendShapes_Eyebrows.fear_M",
      "BlendShapes_Beard.fear_M",
      "BlendShapes_Moustache.fear_M",
      "BlendShapes_Mouth.fear_M",

      // Convention 2
      "BlendShape_Main.Happy",
      "BlendShape_EyeLashes.Happy",
      "BlendShape_Eyebrows.Happy",
      "BlendShape_Beard.Happy",
      "BlendShape_Main.Sad",
      "BlendShape_EyeLashes.Sad",
      "BlendShape_Eyebrows.Sad",
      "BlendShape_Beard.Sad",
      "BlendShape_Main.Angry",
      "BlendShape_EyeLashes.Angry",
      "BlendShape_Eyebrows.Angry",
      "BlendShape_Beard.Angry",
      "BlendShape_Main.Surprise",
      "BlendShape_EyeLashes.Surprise",
      "BlendShape_Eyebrows.Surprise",
      "BlendShape_Beard.Surprise",
      "BlendShape_Main.Fear",
      "BlendShape_EyeLashes.Fear",
      "BlendShape_Eyebrows.Fear",
      "BlendShape_Beard.Fear",
      "BlendShape_Mouth.Fear",

      // Eye look shapes (protected too)
      "BlendShape_EyeR.EyeLookOut_R",
      "BlendShape_EyeR.EyeLookIn_R",
      "BlendShape_EyeR.EyeLookUp_R",
      "BlendShape_EyeR.EyeLookDown_R",
      "BlendShape_EyeL.EyeLookIn_L",
      "BlendShape_EyeL.EyeLookOut_L",
      "BlendShape_EyeL.EyeLookUp_L",
      "BlendShape_EyeL.EyeLookDown_L",
      "Eye_R.Eye_R_lookLeft",
      "Eye_R.Eye_R_lookRight",
      "Eye_R.Eye_R_lookUp",
      "Eye_R.Eye_R_lookDown",
      "Eye_L.Eye_L_lookLeft",
      "Eye_L.Eye_L_lookRight",
      "Eye_L.Eye_L_lookUp",
      "Eye_L.Eye_L_lookDown",
      "Mouth_Eyes_Shapes.eyeLookInRight",
      "Mouth_Eyes_Shapes.eyeLookOutRight",
      "Mouth_Eyes_Shapes.eyeLookUpRight",
      "Mouth_Eyes_Shapes.eyeLookDownRight",
      "Mouth_Eyes_Shapes.eyeLookInLeft",
      "Mouth_Eyes_Shapes.eyeLookOutLeft",
      "Mouth_Eyes_Shapes.eyeLookUpLeft",
      "Mouth_Eyes_Shapes.eyeLookDownLeft",
      "eyeLookInRight",
      "eyeLookOutRight",
      "eyeLookUpRight",
      "eyeLookDownRight",
      "eyeLookInLeft",
      "eyeLookOutLeft",
      "eyeLookUpLeft",
      "eyeLookDownLeft",
      "eyeBlinkLeft",
      "eyeBlinkRight",
    ];

    // Standard phonemes (+ MOUTH)
    // NOTE: still exact-match only. We just include extra common aliases.
    this.standardPhonemes = {
      AAA: ["aaa_M", "AHH", "Ahh", "AA_AE_AH_AO", "Custom_Ah"],
      EH: ["eh_M", "EH", "EE", "L_ER", "EH_IH", "Custom_EE"],
      OHH: ["ohh_M", "OHH", "OH", "OW", "AO", "Custom_OW"],
      UUU: ["uuu_M", "UUU", "OU", "UW", "UW_UH_OW", "Custom_AE"],
      IEE: ["iee_M", "IEE", "EE", "IY", "IY_EY", "Custom_EE"],
      RRR: ["rrr_M", "RRR", "R", "ER", "Custom_R"],
      WWW: ["www_M", "WWW", "W", "OO", "AY_AW_OY", "Custom_W_OO"],
      SSS: ["sss_M", "SSS", "FV", "SH", "SH_ZH_CH_JH", "Custom_Ih"],
      FFF: ["fff_M", "FFF", "FV", "F_V", "Custom_FV"],
      TTH: ["tth_M", "TTH", "Th", "TH", "TH_DH", "Custom_Th"],
      MBP: ["mbp_M", "MBP", "P_B_M", "Custom_B_M_P"],
      TLDN: ["TLDN", "T_D_N", "D_T_N", "Custom_TLDN"],

      // Jaw / mouth-open driver (overlay-safe)
      MOUTH: [
        "MouthOpen",
        "Mouth_Open",
        "MouthOpen_M",
        "Mouth_O",
        "JawOpen",
        "JAWOpen",
        "jawOpen",
        "Jaw_Down",
        "jaw_Down",
        "OpenMouth",
        "openMouth",
      ],
    };

    // Prefixes by part
    this.prefixMappings = {
      head: [
        "BlendShapes_Main.",
        "BlendShape_Main.",
        "Morpher_CC_Base_Body.",
        "Main.",
      ],
      mouth: [
        "BlendShapes_Mouth.",
        "BlendShape_Mouth.",
        "BlendShapes_Jaw.",
        "BlendShape_Jaw.",
        "BlendShapes_Main.", // some rigs keep mouth visemes on main
        "BlendShape_Main.",
        "Main.",
      ],
      beard: ["BlendShapes_Beard.", "BlendShape_Beard.", "BlendShapes_Beards."],
      moustache: ["BlendShapes_Moustache.", "BlendShape_Moustache."],
    };

    // Synchronized movements (kept, but aligned to standardPhonemes)
    this.syncedMovements = {
      AAA: {
        mouth: ["aaa_M", "AHH", "Ahh", "AA_AE_AH_AO", "Custom_Ah"],
        head: ["aaa_M", "AHH", "Ahh", "AA_AE_AH_AO", "Custom_Ah"],
        beard: ["aaa_M", "AHH", "Ahh", "AA_AE_AH_AO"],
        moustache: ["aaa_M", "AHH", "Ahh", "AA_AE_AH_AO"],
      },
      EH: {
        mouth: ["eh_M", "EH", "EE", "L_ER", "EH_IH", "Custom_EE"],
        head: ["eh_M", "EH", "EE", "L_ER", "EH_IH", "Custom_EE"],
        beard: ["eh_M", "EH", "EE", "L_ER"],
        moustache: ["eh_M", "EH", "EE", "L_ER"],
      },
      OHH: {
        mouth: ["ohh_M", "OHH", "OH", "OW", "AO", "Custom_OW"],
        head: ["ohh_M", "OHH", "OH", "OW", "AO", "Custom_OW"],
        beard: ["ohh_M", "OHH", "OH", "OW"],
        moustache: ["ohh_M", "OHH", "OH", "OW"],
      },
      UUU: {
        mouth: ["uuu_M", "UUU", "OU", "UW", "UW_UH_OW", "Custom_AE"],
        head: ["uuu_M", "UUU", "OU", "UW", "UW_UH_OW", "Custom_AE"],
        beard: ["uuu_M", "UUU", "OU", "UW"],
        moustache: ["uuu_M", "UUU", "OU", "UW"],
      },
      IEE: {
        mouth: ["iee_M", "IEE", "EE", "IY", "IY_EY", "Custom_EE"],
        head: ["iee_M", "IEE", "EE", "IY", "IY_EY", "Custom_EE"],
        beard: ["iee_M", "IEE", "EE", "IY"],
        moustache: ["iee_M", "IEE", "EE", "IY"],
      },
      RRR: {
        mouth: ["rrr_M", "RRR", "R", "ER", "Custom_R"],
        head: ["rrr_M", "RRR", "R", "ER", "Custom_R"],
        beard: ["rrr_M", "RRR", "R"],
        moustache: ["rrr_M", "RRR", "R"],
      },
      WWW: {
        mouth: ["www_M", "WWW", "W", "OO", "AY_AW_OY", "Custom_W_OO"],
        head: ["www_M", "WWW", "W", "OO", "AY_AW_OY", "Custom_W_OO"],
        beard: ["www_M", "WWW", "W"],
        moustache: ["www_M", "WWW", "W"],
      },
      SSS: {
        mouth: ["sss_M", "SSS", "FV", "SH", "SH_ZH_CH_JH", "Custom_Ih"],
        head: ["sss_M", "SSS", "FV", "SH", "SH_ZH_CH_JH", "Custom_Ih"],
        beard: ["sss_M", "SSS", "FV", "SH", "SH_ZH_CH_JH"],
        moustache: ["sss_M", "SSS", "FV", "SH", "SH_ZH_CH_JH"],
      },
      FFF: {
        mouth: ["fff_M", "FFF", "FV", "F_V", "Custom_FV"],
        head: ["fff_M", "FFF", "FV", "F_V", "Custom_FV"],
        beard: ["fff_M", "FFF", "FV", "F_V"],
        moustache: ["fff_M", "FFF", "FV", "F_V"],
      },
      TTH: {
        mouth: ["tth_M", "TTH", "Th", "TH", "TH_DH", "Custom_Th"],
        head: ["tth_M", "TTH", "Th", "TH", "TH_DH", "Custom_Th"],
        beard: ["tth_M", "TTH", "Th", "TH", "TH_DH"],
        moustache: ["tth_M", "TTH", "Th", "TH", "TH_DH"],
      },
      MBP: {
        mouth: ["mbp_M", "MBP", "P_B_M", "Custom_B_M_P"],
        head: ["mbp_M", "MBP", "P_B_M", "Custom_B_M_P"],
        beard: ["mbp_M", "MBP", "P_B_M"],
        moustache: ["mbp_M", "MBP", "P_B_M"],
      },
      TLDN: {
        mouth: ["TLDN", "T_D_N", "D_T_N", "Custom_TLDN"],
        head: ["TLDN", "T_D_N", "D_T_N", "Custom_TLDN"],
        beard: ["TLDN", "T_D_N", "D_T_N"],
        moustache: ["TLDN", "T_D_N", "D_T_N"],
      },
      MOUTH: {
        mouth: ["MouthOpen", "JawOpen", "Jaw_Down", "Mouth_Open", "OpenMouth"],
        head: ["MouthOpen", "JawOpen", "Jaw_Down", "Mouth_Open", "OpenMouth"],
        beard: ["MouthOpen", "JawOpen", "Jaw_Down"],
        moustache: ["MouthOpen", "JawOpen", "Jaw_Down"],
      },
    };

    // small cache for speed (keyed by array identity-ish)
    this._availCacheKey = null;
    this._availCacheSet = null;
    this._availCacheFiltered = null;

    // Per-phoneme strength (used when rigs collapse multiple phonemes to one morph)
    this.phonemeStrength = {
      IEE: 1.0, // strong
      EH: 0.65, // softer
    };
  }

  enableDebug(opts) {
    opts = opts || {};
    this._debug = !!(opts.debug === undefined ? true : opts.debug);
    this._maxLogsPerMap =
      opts.maxLogsPerMap === undefined ? 8 : opts.maxLogsPerMap;

    if (this._debug)
      console.log("[BlendShapeMapper] debug enabled", {
        maxLogsPerMap: this._maxLogsPerMap,
        mode: "exact-only",
      });
  }

  isProtectedEmotionShape(name) {
    if (!name) return false;
    const low = String(name).toLowerCase();
    for (let i = 0; i < this.protectedEmotionShapes.length; i++) {
      if (low.indexOf(this.protectedEmotionShapes[i].toLowerCase()) !== -1)
        return true;
    }
    return false;
  }

  _getFilteredAvail(availableBlendShapes) {
    // Cache by reference (fast path). If the caller recreates arrays every frame,
    // caching won’t hit, but nothing breaks.
    if (availableBlendShapes === this._availCacheKey) {
      return {
        list: this._availCacheFiltered,
        set: this._availCacheSet,
      };
    }

    const list = [];
    for (let i = 0; i < availableBlendShapes.length; i++) {
      const s = availableBlendShapes[i];
      if (!this.isProtectedEmotionShape(s)) list.push(s);
    }

    const set = new Set(list);

    this._availCacheKey = availableBlendShapes;
    this._availCacheFiltered = list;
    this._availCacheSet = set;

    return { list, set };
  }

  _baseWeightFor(phoneme, part) {
    // Stronger MBP closures; slightly softer fricatives; jaw driver handled elsewhere.
    let base =
      phoneme === "MBP"
        ? 0.95
        : phoneme === "SSS" || phoneme === "FFF" || phoneme === "TTH"
          ? 0.55
          : phoneme === "TLDN"
            ? 0.58
            : phoneme === "MOUTH"
              ? 0.85
              : 0.62;

    // Usually mouth-specific shapes should dominate head shapes slightly (if both exist).
    if (part === "mouth") base *= 1.08;
    if (part === "head") base *= 0.95;

    // ✅ NEW: EH softer than IEE (helps when both map to same morph like "EE")
    const mul = this.phonemeStrength[phoneme];
    if (typeof mul === "number") base *= mul;

    return Math.max(0.1, Math.min(1.0, base));
  }

  mapPhonemeToBlendShapes(availableBlendShapes, phoneme, part) {
    part = part || "head";
    const variants = this.standardPhonemes[phoneme] || [];
    const prefixes = this.prefixMappings[part] || [];
    const synced =
      (this.syncedMovements[phoneme] && this.syncedMovements[phoneme][part]) ||
      [];
    const matches = [];

    const { list: availList, set: availSet } =
      this._getFilteredAvail(availableBlendShapes);

    const base = this._baseWeightFor(phoneme, part);

    let logs = 0;
    const log = (...a) => {
      if (this._debug && logs < this._maxLogsPerMap) {
        console.log.apply(console, a);
        logs++;
      }
    };

    // EXACT matches only (prefix + variant must exist)
    for (let p = 0; p < prefixes.length; p++) {
      const prefix = prefixes[p];

      for (let v = 0; v < variants.length; v++) {
        const full = prefix + variants[v];
        if (availSet.has(full)) {
          matches.push({ name: full, weight: base });
          log("[BSM] exact match", { phoneme, part, full });
        }
      }

      for (let s = 0; s < synced.length; s++) {
        const full = prefix + synced[s];
        if (availSet.has(full) && !matches.some((m) => m.name === full)) {
          matches.push({ name: full, weight: base });
          log("[BSM] exact synced", { phoneme, part, full });
        }
      }
    }

    // Fallback for MOUTH → use vowels gently if no jaw morphs
    if (matches.length === 0 && phoneme === "MOUTH") {
      const vowels = ["AAA", "OHH", "UUU", "IEE"];
      for (let i = 0; i < vowels.length; i++) {
        const sub = this.mapPhonemeToBlendShapes(
          availableBlendShapes,
          vowels[i],
          part,
        );
        for (let j = 0; j < sub.length; j++) {
          const nm = sub[j].name;
          if (!matches.some((m) => m.name === nm)) {
            matches.push({
              name: nm,
              weight: Math.min(0.6, sub[j].weight),
            });
          }
        }
      }
      log("[BSM] fallback via vowels for MOUTH", { count: matches.length });
    }

    if (this._debug && matches.length === 0) {
      console.warn("[BSM] no matches", {
        phoneme,
        part,
        prefixes: prefixes.slice(),
        variants: variants.slice(),
      });
      console.log("[BSM] sample keys:", availList.slice(0, 12));
    }

    return matches;
  }

  getAvailablePhonemes(availableBlendShapes) {
    const mapping = {};
    const keys = Object.keys(this.standardPhonemes);
    for (let i = 0; i < keys.length; i++) {
      const ph = keys[i];
      const head = this.mapPhonemeToBlendShapes(
        availableBlendShapes,
        ph,
        "head",
      );
      const mouth = this.mapPhonemeToBlendShapes(
        availableBlendShapes,
        ph,
        "mouth",
      );
      const beard = this.mapPhonemeToBlendShapes(
        availableBlendShapes,
        ph,
        "beard",
      );
      const moustache = this.mapPhonemeToBlendShapes(
        availableBlendShapes,
        ph,
        "moustache",
      );
      if (head.length || mouth.length || beard.length || moustache.length) {
        mapping[ph] = { head, mouth, beard, moustache };
      }
    }
    if (this._debug) console.log("[BSM] getAvailablePhonemes()", mapping);
    return mapping;
  }

  // Apply across all parts
  applyPhonemeBlendShape(model, phoneme, weight) {
    weight = typeof weight === "number" ? weight : 0.7;
    if (!model || !model.morphTargetDictionary || !model.morphTargetInfluences)
      return;

    const available = Object.keys(model.morphTargetDictionary);
    const parts = ["head", "mouth", "beard", "moustache"];
    const targets = [];

    for (let i = 0; i < parts.length; i++) {
      const sub = this.mapPhonemeToBlendShapes(available, phoneme, parts[i]);
      for (let j = 0; j < sub.length; j++) {
        if (!targets.some((t) => t.name === sub[j].name)) targets.push(sub[j]);
      }
    }

    let applied = 0;
    for (let i = 0; i < targets.length; i++) {
      const name = targets[i].name;
      const base = targets[i].weight;
      const index = model.morphTargetDictionary[name];

      if (typeof index !== "undefined" && !this.isProtectedEmotionShape(name)) {
        const finalW = Math.min(weight * base, 1.0);
        model.morphTargetInfluences[index] = finalW;
        applied++;

        if (this._debug) {
          console.log("[BSM] set influence", {
            name,
            index,
            baseWeight: base,
            weight,
            finalW,
          });
        }
      }
    }

    if (this._debug && applied === 0)
      console.warn("[BSM] applied 0 influences for phoneme", phoneme);
  }

  resetBlendShapes(model) {
    if (!model || !model.morphTargetInfluences || !model.morphTargetDictionary)
      return;

    const entries = Object.entries(model.morphTargetDictionary);
    for (let i = 0; i < entries.length; i++) {
      const name = entries[i][0];
      const index = entries[i][1];
      if (!this.isProtectedEmotionShape(name))
        model.morphTargetInfluences[index] = 0;
    }
    if (this._debug) console.log("[BSM] resetBlendShapes() done");
  }

  inspectAgainst(model) {
    if (!model || !model.morphTargetDictionary) {
      console.warn("[BSM] inspectAgainst(): no morphTargetDictionary");
      return;
    }
    const keys = Object.keys(model.morphTargetDictionary);
    console.log(
      "[BSM] morph keys =",
      keys.length,
      "sample:",
      keys.slice(0, 20),
    );
    const avail = this.getAvailablePhonemes(keys);
    console.log("[BSM] available phoneme map", avail);
  }
}
